import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject, Subscription } from 'rxjs';
import { map, catchError, filter, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import { RoleService } from './role.service';
import { Role } from '../models/role.model';
import { environment } from '../../../environments/environment';

/**
 * Permission names returned by GET /api/users/me/permissions.
 */
export const PERMISSION_NAMES = {
  SETTINGS_UPDATE: 'SETTINGS_UPDATE',
  REPORTS_READ: 'REPORTS_READ',
  CASE_READ: 'CASE_READ',
  CASE_CREATE: 'CASE_CREATE',
  CASE_UPDATE: 'CASE_UPDATE',
  ASSIGN_CASE: 'ASSIGN_CASE',
  ASSIGN_CASE_SELF: 'ASSIGN_CASE_SELF',
  DOMAIN_VIEW: 'DOMAIN_VIEW',
  DOMAIN_CREATE: 'DOMAIN_CREATE',
  EMAIL_READ: 'EMAIL_READ',
  EMAIL_CREATE: 'EMAIL_CREATE',
  EMAIL_UPDATE: 'EMAIL_UPDATE',
  EMAIL_DELETE: 'EMAIL_DELETE',
  EMAIL_SEND: 'EMAIL_SEND',
  DASH_READ: 'DASH_READ',
  AUDIT_READ: 'AUDIT_READ',
  ROLES_READ: 'ROLES_READ',
  ROLES_CREATE: 'ROLES_CREATE',
  ROLES_UPDATE: 'ROLES_UPDATE',
  ROLES_DELETE: 'ROLES_DELETE',
  USERS_READ: 'USERS_READ',
  USERS_CREATE: 'USERS_CREATE',
  USERS_UPDATE: 'USERS_UPDATE',
  USERS_DELETE: 'USERS_DELETE',
} as const;

/**
 * Permission IDs for different modules/actions (used in role detail / role API payloads).
 * These match the permission IDs used in the role detail component.
 */
export const PERMISSION_IDS = {
  SETTINGS_UPDATE: '6972eae5f54845e775751431',
  REPORTS_READ: '6972eaf4f54845e775751432',
  CASE_READ: '6972eb18f54845e775751435',
  CASE_CREATE: '6972eb2ef54845e775751436',
  CASE_UPDATE: '6972eb37f54845e775751437',
  ASSIGN_CASE: '6972eb5ef54845e775751438',
  ASSIGN_CASE_SELF: '6a87d315f6f4303a90ff8064',
  DOMAIN_VIEW: '6972eb6ef54845e775751439',
  DOMAIN_CREATE: '6972eb78f54845e77575143a',
  EMAIL_READ: '6972eb86f54845e77575143b',
  EMAIL_CREATE: '6972eb90f54845e77575143c',
  EMAIL_UPDATE: '6972eb99f54845e77575143d',
  EMAIL_DELETE: '6972eba2f54845e77575143e',
  EMAIL_SEND: '6aaba9ea80a6f4b7b54babe3',
  DASHBOARD_READ: '6972ebb1f54845e77575143f', // backend name is DASH_READ
  AUDIT_READ: '6972eacef54845e77575142f',
  ROLE_READ: '6972ea24f54845e77575142b',
  ROLE_CREATE: '6972ea89f54845e77575142c',
  ROLE_UPDATE: '6972ea93f54845e77575142d',
  ROLE_DELETE: '6972eaa8f54845e77575142e',
  USER_READ: '6972e7fef54845e775751425',
  USER_CREATE: '6972e82cf54845e775751426',
  USER_UPDATE: '6972e847f54845e775751427',
  USERS_DELETE: '6972e858f54845e775751428',
} as const;

/** Map permission ID to permission name (must match backend permission document names). */
const PERMISSION_ID_TO_NAME: Record<string, string> = {
  [PERMISSION_IDS.SETTINGS_UPDATE]: PERMISSION_NAMES.SETTINGS_UPDATE,
  [PERMISSION_IDS.REPORTS_READ]: PERMISSION_NAMES.REPORTS_READ,
  [PERMISSION_IDS.CASE_READ]: PERMISSION_NAMES.CASE_READ,
  [PERMISSION_IDS.CASE_CREATE]: PERMISSION_NAMES.CASE_CREATE,
  [PERMISSION_IDS.CASE_UPDATE]: PERMISSION_NAMES.CASE_UPDATE,
  [PERMISSION_IDS.ASSIGN_CASE]: PERMISSION_NAMES.ASSIGN_CASE,
  [PERMISSION_IDS.ASSIGN_CASE_SELF]: PERMISSION_NAMES.ASSIGN_CASE_SELF,
  [PERMISSION_IDS.DOMAIN_VIEW]: PERMISSION_NAMES.DOMAIN_VIEW,
  [PERMISSION_IDS.DOMAIN_CREATE]: PERMISSION_NAMES.DOMAIN_CREATE,
  [PERMISSION_IDS.EMAIL_READ]: PERMISSION_NAMES.EMAIL_READ,
  [PERMISSION_IDS.EMAIL_CREATE]: PERMISSION_NAMES.EMAIL_CREATE,
  [PERMISSION_IDS.EMAIL_UPDATE]: PERMISSION_NAMES.EMAIL_UPDATE,
  [PERMISSION_IDS.EMAIL_DELETE]: PERMISSION_NAMES.EMAIL_DELETE,
  [PERMISSION_IDS.EMAIL_SEND]: PERMISSION_NAMES.EMAIL_SEND,
  [PERMISSION_IDS.DASHBOARD_READ]: PERMISSION_NAMES.DASH_READ,
  [PERMISSION_IDS.AUDIT_READ]: PERMISSION_NAMES.AUDIT_READ,
  [PERMISSION_IDS.ROLE_READ]: PERMISSION_NAMES.ROLES_READ,
  [PERMISSION_IDS.ROLE_CREATE]: PERMISSION_NAMES.ROLES_CREATE,
  [PERMISSION_IDS.ROLE_UPDATE]: PERMISSION_NAMES.ROLES_UPDATE,
  [PERMISSION_IDS.ROLE_DELETE]: PERMISSION_NAMES.ROLES_DELETE,
  [PERMISSION_IDS.USER_READ]: PERMISSION_NAMES.USERS_READ,
  [PERMISSION_IDS.USER_CREATE]: PERMISSION_NAMES.USERS_CREATE,
  [PERMISSION_IDS.USER_UPDATE]: PERMISSION_NAMES.USERS_UPDATE,
  [PERMISSION_IDS.USERS_DELETE]: PERMISSION_NAMES.USERS_DELETE,
};

export interface UserPermissionsResponse {
  permissions: string[];
  isRoot: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService implements OnDestroy {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private roleService = inject(RoleService);
  private http = inject(HttpClient);
  private readonly apiBaseUrl = (environment.apiBaseUrl || '').replace(/\/+$/, '');
  private authSubscription?: Subscription;

  // Cache user permission names from GET /api/users/me/permissions (no ROLES_READ required)
  // null = not loaded yet, [] = loaded but empty, [names...] = loaded with permission names
  private userPermissionNamesCache: string[] | null = null;
  private userRoleCache: Role | null = null;
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();
  private isLoadingPermissions = false; // Track if permissions are currently being loaded

  constructor() {
    // Subscribe to auth state changes to clear cache on logout
    this.authSubscription = this.authService.isAuthenticated$.pipe(
      filter(isAuthenticated => !isAuthenticated)
    ).subscribe(() => {
      this.clearCache();
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  /**
   * Load and cache user's permissions from GET /api/users/me/permissions.
   * Does not require ROLES_READ; use this for post-login / app-shell so we never depend on roles API on initial load.
   * @param forceReload If true, force reload even if cache exists
   */
  loadUserPermissions(forceReload: boolean = false): Observable<string[]> {
    // Don't load permissions if user is not authenticated
    if (!this.authService.isAuthenticated()) {
      this.clearCache();
      return of([]);
    }

    // Root users have all permission names
    if (this.authService.isRoot()) {
      const allNames = Object.values(PERMISSION_NAMES);
      this.userPermissionNamesCache = allNames;
      this.permissionsSubject.next(allNames);
      return of(allNames);
    }

    // Return cached permission names if available and not forcing reload
    if (!forceReload && this.userPermissionNamesCache !== null) {
      this.permissionsSubject.next(this.userPermissionNamesCache);
      return of(this.userPermissionNamesCache);
    }

    // Prevent multiple simultaneous loads
    if (this.isLoadingPermissions) {
      return this.permissions$.pipe(
        filter(permissions => permissions.length > 0 || this.userPermissionNamesCache !== null),
        take(1)
      );
    }

    if (forceReload) {
      this.userPermissionNamesCache = null;
    }

    this.isLoadingPermissions = true;
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/users/me/permissions` : '/api/users/me/permissions';
    const token = this.authService.getToken();
    const headers = new HttpHeaders({ Authorization: token || '' });

    return this.http.get<UserPermissionsResponse>(url, { headers }).pipe(
      map((res) => {
        // Normalize to uppercase so we match regardless of backend casing (e.g. "dash_read" -> "DASH_READ")
        const names = (res.permissions || []).map((n: string) =>
          String(n).trim().toUpperCase().replace(/\s+/g, '_')
        );
        this.userPermissionNamesCache = names;
        this.isLoadingPermissions = false;
        this.permissionsSubject.next(names);
        return names;
      }),
      catchError((error) => {
        this.isLoadingPermissions = false;
        this.userPermissionNamesCache = null;
        this.permissionsSubject.next([]);
        return of([]);
      })
    );
  }

  /**
   * Resolve permission ID to permission name (for backward compatibility with ID-based callers).
   */
  private resolveToName(permissionIdOrName: string): string {
    return PERMISSION_ID_TO_NAME[permissionIdOrName] ?? permissionIdOrName;
  }

  /**
   * Check if user has a specific permission (by ID or by name).
   * @param permissionIdOrName Permission ID (e.g. PERMISSION_IDS.ROLE_READ) or name (e.g. PERMISSION_NAMES.ROLES_READ)
   */
  hasPermission(permissionIdOrName: string): boolean {
    if (this.authService.isRoot()) {
      return true;
    }
    if (!this.userPermissionNamesCache) {
      this.loadUserPermissions().subscribe();
      return false;
    }
    const name = this.resolveToName(permissionIdOrName);
    return this.userPermissionNamesCache.includes(name);
  }

  /**
   * Check if user has a specific permission by name (e.g. ROLES_READ for menu).
   */
  hasPermissionByName(permissionName: string): boolean {
    return this.hasPermission(permissionName);
  }

  /**
   * Check if user has a specific permission (async).
   * @param permissionIdOrName Permission ID or name
   */
  hasPermissionAsync(permissionIdOrName: string): Observable<boolean> {
    if (this.authService.isRoot()) {
      return of(true);
    }
    if (this.userPermissionNamesCache) {
      const name = this.resolveToName(permissionIdOrName);
      return of(this.userPermissionNamesCache.includes(name));
    }
    return this.loadUserPermissions().pipe(
      map((names) => names.includes(this.resolveToName(permissionIdOrName)))
    );
  }

  /**
   * Clear cached permissions (call on logout)
   */
  clearCache(): void {
    this.userPermissionNamesCache = null;
    this.userRoleCache = null;
    this.permissionsSubject.next([]);
  }

  /**
   * Get cached user permission names (for route guards and menu).
   */
  getCachedPermissions(): string[] {
    if (this.authService.isRoot()) {
      return Object.values(PERMISSION_NAMES);
    }
    return this.userPermissionNamesCache || [];
  }
}
